import { getTranslatorWithFallback } from '..'
import type { NextIntlKeys } from '../types'
import type { GetErrorMap } from '@/helpers/operations'

function getIssueValidationTranslationKey(validation: string): NextIntlKeys<'nextjs'> {
  switch (validation) {
    case 'email':
    case 'url':
    case 'uuid':
      return `errors.messages.invalid_${validation}`
    case 'regex':
      return `errors.messages.invalid_value`
    default:
      return 'errors.messages.invalid_value'
  }
}

const getGlobalErrorMap: GetErrorMap = (t_) => {
  const t = getTranslatorWithFallback(t_)
  return (issue) => {
    try {
      switch (issue.code) {
        case 'invalid_value':
          /**
           * v4 does not further support ZodIssueCode.invalid_literal or ZodIssueCode.invalid_enum_value
           * z.enum and z.literal can both be an array of values, so in general returning the error message based on length works:
           */
          if (issue.values.length === 1) {
            return t('errors.messages.invalid_literal', {
              literal: JSON.stringify(issue.values[0]),
              received: JSON.stringify(issue.input),
            })
          }
          return t('errors.messages.invalid_enum_value', {
            options: issue.values.join(', '),
          })
        case 'invalid_format': {
          if (issue.path && issue.path.includes('video_start_time')) {
            if (issue.format === 'regex') {
              return t('donate.errors.youtube_timestamp_malformed')
            }
          }
          return t(getIssueValidationTranslationKey(issue.format))
        }
        case 'not_multiple_of':
          return t(`errors.messages.not_multiple_of`, {
            multipleOf: issue.divisor.toString(),
          })
        case 'too_small':
        case 'too_big': {
          const constraint = (issue.code === 'too_small' ? issue.minimum : issue.maximum).toString()
          if (issue.origin === 'number') {
            const inclusiveSuffix = issue.inclusive ? '_inclusive' : ''
            return t(`errors.messages.${issue.origin as 'number'}.${issue.code}${inclusiveSuffix}`, {
              constraint,
            })
          }
          if (issue.origin === 'string') {
            return t(`errors.messages.${issue.origin as 'string'}.${issue.code}`, {
              constraint,
            })
          }
          break
        }
        case 'custom':
          return t('errors.messages.default')
        default: {
          const FALLBACK = 'fallback'
          const translation = t(`errors.messages.${issue.code}` as NextIntlKeys<'nextjs'>, {
            defaultValue: FALLBACK,
          })
          if (translation === FALLBACK) return
          return translation
        }
      }
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      console.error('Error localising validation', (e as any).message)
      return
    }
  }
}

export default getGlobalErrorMap
